import { useParams, Link, Navigate } from 'react-router-dom';
import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PageMeta, BreadcrumbJsonLd, FAQJsonLd } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MapPin, Clock, Users, Dumbbell, ArrowRight, Check, Star, Navigation2 } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { RelatedArticles } from '@/components/RelatedArticles';
import { nearbyAreas, getCityContent } from '@/data/seoLocal';
import { Helmet } from 'react-helmet-async';

export default function CityLandingPage() {
  const { citySlug } = useParams();
  const area = nearbyAreas.find(a => a.slug === citySlug);

  const [heroRef, heroVisible] = useScrollReveal(0.1);
  const [contentRef, contentVisible] = useScrollReveal(0.1);
  const [progsRef, progsVisible] = useScrollReveal(0.1);
  const [ctaRef, ctaVisible] = useScrollReveal(0.1);

  if (!area) return <Navigate to="/" replace />;

  const content = getCityContent(area.city, area.distance, area.slug);
  const otherCities = nearbyAreas.filter(a => a.slug !== citySlug && a.slug !== 'saint-chamond').slice(0, 6);

  const localSchema = {
    "@context": "https://schema.org",
    "@type": "ExerciseGym",
    "name": "CrossFit Saint-Chamond",
    "alternateName": "HYROX Training Club Saint-Chamond",
    "description": content.metaDesc,
    "url": `https://crossfit-saintchamond.fr/crossfit/${citySlug}`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "7 Rue Jean-Jacques Rousseau",
      "addressLocality": "Saint-Chamond",
      "postalCode": "42400",
      "addressRegion": "Loire",
      "addressCountry": "FR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 45.4783,
      "longitude": 4.5170
    },
    "email": "crossfit.saintchamond@gmail.com",
    "areaServed": {
      "@type": "City",
      "name": area.city,
      "postalCode": area.code
    },
    "sameAs": [
      "https://www.instagram.com/crossfit_saintchamond",
      "https://www.facebook.com/CrossFitSaintChamond"
    ],
    "priceRange": "59€ - 99€/mois",
    "knowsAbout": ["CrossFit", "HYROX", "Functional Training", "WOD", "Coaching sportif"],
    "founder": { "@type": "Person", "name": "Nicolas Perroy" }
  };

  return (
    <div className="min-h-screen">
      <PageMeta
        title={`CrossFit près de ${area.city}`}
        description={content.metaDesc}
        path={`/crossfit/${citySlug}`}
      />
      <BreadcrumbJsonLd items={[
        { name: 'Accueil', url: '/' },
        { name: 'Zones desservies', url: '/zones-desservies' },
        { name: `CrossFit ${area.city}` },
      ]} />
      {content.localFaq && content.localFaq.length > 0 && <FAQJsonLd faqs={content.localFaq} />}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(localSchema)}</script>
      </Helmet>
      <TopBar />
      <Navigation />

      {/* Hero */}
      <section ref={heroRef} className="relative bg-dark overflow-hidden" data-testid="city-hero">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_063f6b75-858e-4561-abbf-3c8b3baa3d11/artifacts/hmmuue2f_WhatsApp%20Image%202026-04-02%20at%2014.38.19.jpeg" alt={`CrossFit près de ${area.city}`} className="w-full h-full object-cover opacity-25" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/80 to-dark/50" />
        </div>
        <div className="container-custom relative z-10 py-16 md:py-24">
          <div className={`max-w-3xl transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-2 text-white/50 text-sm mb-4">
              <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
              <span>/</span>
              <span className="text-white/70">CrossFit {area.city}</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-primary/20 border border-primary/30 rounded-full px-4 py-1.5">
                <span className="text-primary text-xs font-bold">{area.distance} de {area.city}</span>
              </div>
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-white leading-[0.9] mb-6">
              CROSSFIT PRÈS DE<br /><span className="text-primary">{area.city.toUpperCase()}</span>
            </h1>
            <p className="text-white/60 text-base max-w-xl leading-relaxed">
              {content.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section ref={contentRef} className="section-padding bg-white" data-testid="city-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className={`lg:col-span-2 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h2 className="font-heading text-3xl text-dark mb-6">
                VOTRE SALLE DE CROSSFIT À {area.distance} DE {area.city.toUpperCase()}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{content.why}</p>

              <h3 className="font-heading text-xl text-dark mb-4 mt-10">POURQUOI CHOISIR CROSSFIT SAINT-CHAMOND ?</h3>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  'Coachs certifiés CrossFit',
                  'HYROX Training Club officiel',
                  'Accessible à tous les niveaux',
                  'Cours du lundi au samedi',
                  'Free Access inclus (3x et Illimité)',
                  'Communauté soudée et bienveillante',
                  'Équipement professionnel complet',
                  'À partir de 59€/mois sans engagement',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                    <span className="text-sm text-foreground">{item}</span>
                  </div>
                ))}
              </div>

              <h3 className="font-heading text-xl text-dark mb-4">COMMENT VENIR DEPUIS {area.city.toUpperCase()} ?</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">{content.transport}</p>

              {content.neighborhoods && content.neighborhoods.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-heading text-sm text-dark mb-3 tracking-wider">QUARTIERS DESSERVIS</h4>
                  <div className="flex flex-wrap gap-2">
                    {content.neighborhoods.map((n) => (
                      <span key={n} className="text-xs bg-accent text-primary px-3 py-1.5 rounded-full font-medium">
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-background rounded-xl p-6 mt-6">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-dark mb-1">Adresse</h4>
                    <p className="text-sm text-muted-foreground">7 Rue Jean-Jacques Rousseau, 42400 Saint-Chamond</p>
                    <a href="mailto:crossfit.saintchamond@gmail.com" className="text-sm text-primary hover:underline mt-1 block">crossfit.saintchamond@gmail.com</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className={`transition-all duration-700 delay-200 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <Card className="p-6 bg-primary text-white sticky top-28">
                <h3 className="font-heading text-xl mb-4">ESSAYEZ GRATUITEMENT</h3>
                <p className="text-white/80 text-sm mb-6">
                  Vous habitez à {area.city} ? Venez essayer un cours gratuitement et découvrir notre box.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-white/80 text-sm">
                    <Navigation2 className="h-4 w-4 text-white/60" />
                    <span>{area.distance} de {area.city}</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/80 text-sm">
                    <Clock className="h-4 w-4 text-white/60" />
                    <span>Lun-Ven : 9h-20h30 | Sam : 9h30-11h30</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/80 text-sm">
                    <Dumbbell className="h-4 w-4 text-white/60" />
                    <span>WOD, HYROX, Team WOD, Free Access</span>
                  </div>
                </div>
                <Link to="/contact">
                  <Button size="lg" className="w-full bg-white hover:bg-white/90 text-primary font-heading tracking-wide py-5 rounded-lg">
                    NOUS CONTACTER
                  </Button>
                </Link>
                <Link to="/planning" className="block mt-3">
                  <Button size="lg" variant="outline" className="w-full border-white/30 text-white hover:bg-white/10 font-heading tracking-wide py-5 rounded-lg bg-transparent">
                    VOIR LE PLANNING
                  </Button>
                </Link>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Programmes */}
      <section ref={progsRef} className="section-padding bg-background" data-testid="city-programmes">
        <div className="container-custom">
          <div className={`text-center mb-12 transition-all duration-700 ${progsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="font-heading text-3xl text-dark mb-3">NOS PROGRAMMES</h2>
            <p className="text-muted-foreground">À {area.distance} de {area.city}, profitez de tous nos cours</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: 'CrossFit WOD', slug: 'crossfit-wod', desc: 'Entraînement fonctionnel quotidien' },
              { name: 'HYROX', slug: 'hyrox', desc: 'Préparation compétition fitness' },
              { name: 'Team WOD', slug: 'team-wod', desc: 'Entraînement collectif en équipe' },
              { name: 'Free Access', slug: 'free-access', desc: 'Entraînement libre avec équipement' },
            ].map((prog) => (
              <Link key={prog.slug} to={`/cours/${prog.slug}/${citySlug}`} className="group">
                <Card className="p-5 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 h-full">
                  <div className="text-xs text-primary font-semibold tracking-wider uppercase mb-1">près de {area.city}</div>
                  <h3 className="font-heading text-base text-dark mb-1 group-hover:text-primary transition-colors">{prog.name}</h3>
                  <p className="text-muted-foreground text-xs">{prog.desc}</p>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Local FAQ */}
      {content.localFaq && content.localFaq.length > 0 && (
        <section className="section-padding bg-background" data-testid="city-faq">
          <div className="container-custom max-w-3xl">
            <h2 className="font-heading text-2xl md:text-3xl text-dark mb-8 text-center">
              QUESTIONS FRÉQUENTES — {area.city.toUpperCase()}
            </h2>
            <div className="space-y-4">
              {content.localFaq.map((faq, i) => (
                <Card key={i} className="p-5 border-border/60">
                  <h3 className="font-heading text-base text-dark mb-2">{faq.q}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other Cities */}
      <section className="section-padding bg-white" data-testid="other-cities">
        <div className="container-custom">
          <h2 className="font-heading text-2xl text-dark mb-8 text-center">CROSSFIT ACCESSIBLE DEPUIS</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {otherCities.map((c) => (
              <Link key={c.slug} to={`/crossfit/${c.slug}`} className="bg-background hover:bg-accent text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                CrossFit {c.city} <span className="text-muted-foreground text-xs">({c.distance})</span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/zones-desservies" className="text-sm text-primary hover:underline font-semibold">
              Voir toutes les zones desservies →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="bg-primary py-16" data-testid="city-cta">
        <div className="container-custom text-center">
          <div className={`transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
              ENVIE D'ESSAYER ? À SEULEMENT {area.distance} DE {area.city.toUpperCase()}
            </h2>
            <p className="text-white/80 text-base mb-8 max-w-lg mx-auto">
              Rejoignez CrossFit Saint-Chamond et découvrez le CrossFit et le HYROX dans une ambiance unique.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white hover:bg-white/90 text-primary font-heading tracking-wide px-10 py-7 rounded-xl transition-all duration-200 hover:scale-[1.03]">
                NOUS CONTACTER
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related articles */}
      <RelatedArticles
        keywords={['CrossFit', 'Saint-Chamond', area.city, 'HYROX']}
        title={`À lire — CrossFit près de ${area.city}`}
        limit={3}
      />

      <Footer />
    </div>
  );
}
